import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from "react";
import { ButtonLink, TabLink } from "./button/Button";
import { NOOP } from "../utils/Constants";
import { convertToId } from "../utils/util";

// Define types for the tab and context
interface Tab {
  name: string;
  content: ReactNode;
  headerAction?: ReactNode; // Optional property for any action in the header
}

interface TabsContextType {
  baseClassName: string;
  activeTab: string;
  setTab: (tabName: string) => void;
}

interface TabsPanelProps {
  tabs: Tab[];
  baseClassName: string;
  defaultTab?: string;
  callback?: (activeTab: string) => void;
  hideTabs?: boolean;
  scrollToTopEnable?: boolean;
}

export const TabsContext = createContext<TabsContextType | undefined>(
  undefined,
);

export function TabSwitchPanel({
  tabs,
  baseClassName,
  defaultTab,
  callback = NOOP,
  hideTabs,
  scrollToTopEnable = true,
}: TabsPanelProps) {
  const [activeTab, setTab] = useState<string>(defaultTab || tabs[0]?.name);
  const [content, setContent] = useState<ReactNode>();
  const tabRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const foundTab = tabs.find((tab) => tab.name === activeTab);
    if (foundTab) {
      setContent(foundTab.content);
    }
    callback(activeTab);
  }, [tabs, activeTab, callback]);

  useEffect(() => {
    if (scrollToTopEnable) {
      scrollToTop();
    }
  }, [activeTab, scrollToTopEnable]);

  const scrollToTop = () => {
    const element = tabRef.current;
    if (element) {
      element.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  };

  return (
    <div className="tab-container" ref={tabRef}>
      {!hideTabs && (
        <TabSwitch
          baseClassName={baseClassName}
          activeTab={activeTab}
          setTab={setTab}
        >
          {tabs.map((tab) => (
            <Tab key={tab.name} name={tab.name}>
              {tab.name}
              {tab.headerAction && tab.headerAction}
            </Tab>
          ))}
        </TabSwitch>
      )}
      <div className="tab-wrapper dash-main-content">{content}</div>
    </div>
  );
}

interface TabsProps {
  baseClassName: string;
  activeTab: string;
  setTab: (tabName: string) => void;
  children: ReactNode;
}

export function TabSwitch({
  baseClassName,
  activeTab,
  setTab,
  children,
  ...rest
}: TabsProps) {
  const ctx = { baseClassName, activeTab, setTab };
  return (
    <TabsContext.Provider value={ctx}>
      <div className={`header-tabs ${baseClassName}-tabs`} {...rest}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

interface TabProps {
  name: string;
  children: ReactNode;
}

export function Tab({ name, children }: TabProps) {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("Tab must be used within a TabsProvider");
  }

  const { baseClassName, activeTab, setTab } = context;
  const activeClass = activeTab === name ? " active" : "";

  return (
    <TabLink
      id={convertToId(name)}
      variant="tab"
      color="primary"
      className={`${baseClassName}-tab${activeClass}`}
      onClick={() => setTab(name)}
    >
      {children}
    </TabLink>
  );
}
